import React, { useCallback, useState, useContext } from 'react';
import { IQuinoComponentProps, ObjectBookmark, QuinoLabeled, useService, useMetadata } from '@quino/ui';
import { ILogger, QuinoCoreServiceSymbols, getMetaProperty } from '@quino/core';
import { TextArea } from 'devextreme-react';
import { ICustomActionService } from '../../api';
import { SHARED_SERVICE_IDENTIFIER } from '../../ioc/sharedIdentifiers';
import { useI18n } from '../../lang';
import { Popup } from 'devextreme-react/popup';
import { TextBox } from 'devextreme-react/text-box';
import { PopupButtonBar } from '../Util';
import { Button } from 'devextreme-react/button';
import { ProfileContext } from '../../contexts';

type TStyles = {
    popup: string;
};
const styles: TStyles = require('./CommentButton.less');
const actionToExecute = 'ambcust_CreatePortalTask';

export const CommentButton: React.FunctionComponent<IQuinoComponentProps<ObjectBookmark>> = (
    props,
) => {
    const { bookmark, element } = props;
    const { metaClass, primaryKey } = bookmark.genericObject;

    const metaProperty = getMetaProperty(element);
    const { enabled, readOnly } = useMetadata(metaProperty, bookmark.genericObject);

    const [profileContext]  = useContext(ProfileContext);

    const customActionService = useService<ICustomActionService>(
        SHARED_SERVICE_IDENTIFIER.ICUSTOMACTIONSERVICE,
    );
    const logger = useService<ILogger>(QuinoCoreServiceSymbols.ILogger);

    const i18n = useI18n();
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [isExecuting, setIsExecution] = useState(false);
    const [showPopup, setShowPopup] = useState(false);

    const onSubmit = useCallback(async () => {
        try {
            let contactId = "";
            const primaryKeyParts = primaryKey && primaryKey.split("=");
            if (primaryKeyParts && primaryKeyParts.length === 2) {
                contactId = primaryKeyParts[1];
            }
            
            if (!contactId) {
                throw new Error("Invalid primary key");
            }
            
            setIsExecution(true);
            await customActionService.executeWebCustomActionAsync(
                actionToExecute,
                { createdbyportaluser: profileContext.emailAddress, contactid: contactId, subject: subject, description: description },
            );
        } catch (e) {
            logger.logWarn(`The comment button did execute with errors [${JSON.stringify(e)}]`);
        } finally {
            setIsExecution(false);
            setShowPopup(false);
            setSubject('');
            setDescription('');
            bookmark.reload();
        }
    }, [subject, setSubject, description, setDescription, setIsExecution, setShowPopup, customActionService, primaryKey, logger]);

    if (!primaryKey || metaClass != 'contact') {
        return <></>;
    }

    return (
        <>
            <Button
                type={'default'}
                stylingMode={'outlined'}
                text={i18n.t('literal.CustomLiterals.CommentButton.Title')}
                disabled={isExecuting || readOnly || !enabled}
                onClick={() => setShowPopup(true)}
            />
            <Popup
                visible={showPopup}
                showCloseButton={false}
                title={i18n.t('literal.CustomLiterals.CommentButton.Title')}
                width={500}
                height={500}
                dragEnabled={false}
                className={styles.popup}
            >
                <div id={'popup-action'}>
                    <QuinoLabeled
                        label={i18n.t('literal.CustomLiterals.CommentButton.Subject')}
                        required={true}
                    >
                        <TextBox
                            value={subject}
                            disabled={isExecuting}
                            valueChangeEvent={'keyup blur'}
                            onValueChanged={(e) => setSubject(e.value)}
                        />
                    </QuinoLabeled>
                    <QuinoLabeled
                        label={i18n.t('literal.CustomLiterals.CommentButton.Description')}
                        required={true}
                    >
                        <TextArea
                            value={description}
                            disabled={isExecuting}
                            height={250}
                            valueChangeEvent={'keyup blur'}
                            onValueChanged={(e) => setDescription(e.value)}
                        />
                    </QuinoLabeled>
                </div>
                <PopupButtonBar>
                    <Button
                        onClick={() => setShowPopup(false)}
                        stylingMode={'outlined'}
                        type={'default'}
                        icon={'close'}
                        text={i18n.t('literal.CustomLiterals.CommentButton.Cancel')}
                    />
                    <Button
                        onClick={onSubmit}
                        disabled={!subject || !description || isExecuting}
                        stylingMode={'contained'}
                        type={'default'}
                        icon={'save'}
                        text={i18n.t('literal.CustomLiterals.CommentButton.Confirm')}
                    />
                </PopupButtonBar>
            </Popup>
        </>
    );
};
